import { store, store$ } from '@redux/store';
import { distinctUntilChanged, filter, fromEvent, map, Subject } from 'rxjs';
import fastdom from 'fastdom';
import { setAntiForgeryToken } from '../services/antiForgeryTokenService';
import formatPhoneNumber from '../utils/formatPhoneNumber';
import BookingError from '../enums/bookingError';
import { getStoredUserData, storeUserData } from '../services/userService';
import ModalStep from '../enums/modalStep';
import getSpecDetails from '../apis/getSpecDetails';
import {
  closeModalIconSvgElement,
  nhsLogoSvgElement
} from '../constants/svgIcons';
import { selectBookingError, selectModalStep } from '../redux/selectors';
import paneService from '../services/paneService';
import getVerificationMethods from '../apis/getVerificationMethods';
import { bookingReset, closeModal } from '../redux/slices';
import Footer from './footer';
import { processVerificationMethods } from '../services/verificationMethodService';
import verifyUserEmail$ from '../apis/verifyUserEmail';
import getTourRelatedQuestions$ from '../apis/getTourRelatedQuestions';
import InnerModal from './innerModal';

const primaryButtonTextMap = new Map();
primaryButtonTextMap.set(ModalStep.SpecSelection, 'Continue');
primaryButtonTextMap.set(ModalStep.DateTimeSelection, 'Continue');
primaryButtonTextMap.set(ModalStep.TourConfiguration, 'Continue');
primaryButtonTextMap.set(ModalStep.TermsAndConditions, 'Accept and continue');
primaryButtonTextMap.set(ModalStep.Questions, 'Schedule my self-tour');
primaryButtonTextMap.set(ModalStep.Confirmation, null);
primaryButtonTextMap.set(ModalStep.SignUp, 'Sign up');
primaryButtonTextMap.set(ModalStep.SignIn, 'Sign in');
primaryButtonTextMap.set(ModalStep.PhoneVerification, 'Send code');
primaryButtonTextMap.set(ModalStep.IdentityVerification, null);
primaryButtonTextMap.set(ModalStep.CreditCardVerification, 'Continue');
primaryButtonTextMap.set(ModalStep.IframeVerification, null);
primaryButtonTextMap.set(ModalStep.VerificationInProgress, null);

const secondaryButtonTextMap = new Map();
secondaryButtonTextMap.set(ModalStep.SpecSelection, null);
secondaryButtonTextMap.set(ModalStep.DateTimeSelection, 'Call for assistance');
secondaryButtonTextMap.set(ModalStep.TourConfiguration, 'Back');
secondaryButtonTextMap.set(ModalStep.TermsAndConditions, 'Back');
secondaryButtonTextMap.set(ModalStep.Questions, null);
secondaryButtonTextMap.set(ModalStep.Confirmation, null);
secondaryButtonTextMap.set(ModalStep.SignIn, 'Back');
secondaryButtonTextMap.set(ModalStep.SignUp, 'Back');
secondaryButtonTextMap.set(ModalStep.PhoneVerification, 'Back');
secondaryButtonTextMap.set(ModalStep.IdentityVerification, 'Back');
secondaryButtonTextMap.set(ModalStep.CreditCardVerification, 'Back');
secondaryButtonTextMap.set(ModalStep.IframeVerification, 'Back');
secondaryButtonTextMap.set(ModalStep.VerificationInProgress, 'Back');

export default class SelfGuidedTourModal {
  specData = null;

  closeButton = null;

  complementaryPaneSelector = '[data-self-guided-tour-complementary]';

  contentWrapper = null;

  currentStep = null;

  dialogElement = null;

  mainPaneSelector = '[data-self-guided-tour-main]';

  otherTourableHomes = {};

  constructor() {
    this.dialogElement = document.querySelector(
      '[data-self-guided-tour-dialog]'
    );
    this.contentWrapper = this.dialogElement.querySelector(
      '[data-dialog-content]'
    );

    this.stepChange$ = store$.pipe(
      map(selectModalStep),
      distinctUntilChanged()
    );
    this.stepChange$.subscribe(this.onStepChange);
    this.verificationMethods = [];
    this.tourQuestions = [];
    this.unavailableTours = false;
    this.presentErrorState$ = new Subject();
    this.onUserChange$ = new Subject();
    this.onUserStatusChange$ = new Subject();
    this.onUserChange$.subscribe(user => {
      storeUserData(user);
    });
    this.onUserStatusChange$.subscribe(userStatus => {
      this.userStatus = userStatus;
    });
    this.verificationInfo = {};

    const jsonContainer = document.querySelector(
      '[data-self-guided-tour-data]'
    );
    const jsonData = jsonContainer ? JSON.parse(jsonContainer.innerHTML) : {};
    const { stripeApiKey, ...specData } = jsonData;
    this.specData = specData;
    this.stripeApiKey = stripeApiKey;

    this.getInitialData();
    setAntiForgeryToken(SelfGuidedTourModal.getRequestVerificationToken());

    this.footer = new Footer();
    this.innerModal = new InnerModal(this.innerModalShow, this.innerModalHide);

    fromEvent(this.dialogElement, 'cancel').subscribe(() => {
      store.dispatch(closeModal());
    });

    store$
      .pipe(
        map(selectBookingError),
        distinctUntilChanged(),
        filter(bookingError => bookingError !== null)
      )
      .subscribe(this.handleBookingError);
  }

  getInitialData() {
    if (!this.specData.specId || this.specData.specId === '0') {
      return;
    }

    this.verificationMethods$ = getVerificationMethods(this.specData.specId);
    this.specDetails$ = getSpecDetails(this.specData.specId);
    this.tourQuestions$ = getTourRelatedQuestions$(this.specData.specId);

    this.specDetails$?.subscribe({
      next: details => {
        if (details.error) {
          this.unavailableTours = true;
          return;
        }
        this.specData.salesNumber = details.SalesPhone;
        this.specData.isForRent = details.IsForRent;
        this.specData.supportEmail = details.SupportEmailAddress;
        this.specData.termsAndConditionsSrc =
          details.TermsAndConditionsiFrameUrl;
        this.specData.privacyPolicyHref = details.PrivacyPolicyUrl;
      }
    });
    this.verificationMethods$
      ?.pipe(
        filter(({ error }) => !error),
        map(processVerificationMethods)
      )
      .subscribe(this.setAvailableVerificationMethods);

    this.tourQuestions$?.pipe(filter(({ error }) => !error)).subscribe({
      next: questions => {
        const questionsCollection = {};
        if (questions && !questions.error) {
          questions.forEach(item => {
            questionsCollection[item.Id] = item;
          });
        }
        this.tourQuestions = questionsCollection;
      }
    });
  }

  static getRequestVerificationToken() {
    const tokenInput = document.querySelector(
      'input[name="__RequestVerificationToken"]'
    );
    if (!tokenInput) {
      return null;
    }
    return tokenInput.value;
  }

  /**
   * Binds event handlers for the footer component.
   * Subscribes to primary and secondary button click events and associates them with their respective handlers.
   */
  bindEvents() {
    this.footer.bindEvents();
    this.footer.onPrimaryButtonClick$.subscribe(this.handlerPrimaryButtonClick);
    this.footer.onSecondaryButtonClick$.subscribe(
      this.handlerSecondaryButtonClick
    );
  }

  /**
   * Verifies the user's email address by sending a request with the provided email
   * and the current spec ID.
   *
   * @param {string} email - The email address to verify.
   * @returns {Observable} An observable that emits the result of the email verification.
   */
  verifyUserEmail$(email) {
    return verifyUserEmail$({
      specId: this.specData.specId,
      email
    });
  }

  updateUserData(user) {
    this.onUserStatusChange$.next(user);
  }

  /**
   * Disables scrolling on the dialog element when the inner modal is shown.
   */
  innerModalShow = () => {
    this.disableScroll();
  };

  /**
   * Enables scrolling on the dialog element when the inner modal is hidden.
   */
  innerModalHide = () => {
    this.enableScroll();
  };

  /**
   * Sets the available verification methods for the self-guided tour modal.
   *
   * @param {Array} verificationMethods - An array of verification methods to be set.
   */
  setAvailableVerificationMethods = verificationMethods => {
    this.verificationMethods = verificationMethods;
  };

  /**
   * Retrieves the main panel for the self-guided tour.
   *
   * @returns {Object} The main panel object for the current step of the self-guided tour.
   */
  getMainPanel() {
    return paneService(this.currentStep, 'main', this);
  }

  /**
   * Disables scrolling by adding a CSS class to the dialog element.
   * The class applied is expected to hide overflow, preventing the user
   * from scrolling the content.
   */
  disableScroll = () => {
    this.dialogElement.classList.add('nhs-u-overflow--hidden');
  };

  /**
   * Enables scrolling by removing the 'nhs-u-overflow--hidden' class
   * from the dialog element. This allows the content to become scrollable again.
   */
  enableScroll = () => {
    this.dialogElement.classList.remove('nhs-u-overflow--hidden');
  };

  /**
   * Handles the click event for the primary button.
   * Retrieves the main panel and triggers its primary action handler.
   */
  handlerPrimaryButtonClick = () => {
    const panel = this.getMainPanel();
    panel.handlePrimaryAction?.();
  };

  /**
   * Handles the click event for the secondary button.
   * Retrieves the main panel and triggers the secondary action handler.
   */
  handlerSecondaryButtonClick = () => {
    const panel = this.getMainPanel();
    panel.handleSecondaryAction?.();
  };

  callSales = () => {
    const { salesNumber } = this.getSpecDetails();
    if (salesNumber) {
      window.location.href = `tel:${salesNumber}`;
    }
  };

  getSpecDetails() {
    return this.specData;
  }

  get otherHomes() {
    return this.otherTourableHomes;
  }

  set otherHomes(value) {
    this.otherTourableHomes = value;
  }

  /**
   * Sets the dialog step class based on the current step.
   * if step is falsy, it removes all step classes.
   *
   * @param [step] {ModalStep} step - The modal step to apply the class.
   */
  setDialogStepClass(step) {
    const stepClassMap = {
      [ModalStep.SpecSelection]: ['nhs-c-self-guided-modal--spec-selection'],
      [ModalStep.DateTimeSelection]: [
        'nhs-c-self-guided-modal--date-time-selection'
      ],
      [ModalStep.TourConfiguration]: [
        'nhs-c-self-guided-modal--tour-configuration'
      ],
      [ModalStep.TermsAndConditions]: [
        'nhs-c-self-guided-modal--terms-and-conditions'
      ],
      [ModalStep.Questions]: [
        'nhs-c-self-guided-modal--questions',
        'nhs-mq-self-guided-modal--questions-large'
      ],
      [ModalStep.Confirmation]: ['nhs-c-self-guided-modal--confirmation'],
      [ModalStep.SignIn]: [
        'nhs-c-self-guided-modal__wrapper--auth',
        'nhs-mq-self-guided-modal__wrapper--auth-large',
        'nhs-c-self-guided-modal--sign-in'
      ],
      [ModalStep.SignUp]: [
        'nhs-c-self-guided-modal__wrapper--auth',
        'nhs-mq-self-guided-modal__wrapper--auth-large',
        'nhs-c-self-guided-modal--sign-up'
      ],
      [ModalStep.PhoneVerification]: [
        'nhs-c-self-guided-modal__wrapper--auth',
        'nhs-mq-self-guided-modal__wrapper--auth-large',
        'nhs-c-self-guided-modal--phone-verification'
      ],
      [ModalStep.IdentityVerification]: [
        'nhs-c-self-guided-modal__wrapper--auth',
        'nhs-mq-self-guided-modal__wrapper--auth-large',
        'nhs-c-self-guided-modal--identity-verification'
      ],
      [ModalStep.CreditCardVerification]: [
        'nhs-c-self-guided-modal__wrapper--auth',
        'nhs-mq-self-guided-modal__wrapper--auth-large',
        'nhs-c-self-guided-modal--credit-card-verification'
      ],
      [ModalStep.IframeVerification]: [
        'nhs-c-self-guided-modal__wrapper--iframe',
        'nhs-mq-self-guided-modal__wrapper--iframe-large'
      ],
      [ModalStep.VerificationInProgress]: [
        'nhs-c-self-guided-modal__wrapper--auth',
        'nhs-mq-self-guided-modal__wrapper--auth-large'
      ]
    };

    fastdom.mutate(() =>
      this.dialogElement.classList.remove(...Object.values(stepClassMap).flat())
    );

    if (step && stepClassMap[step]) {
      fastdom.mutate(() =>
        this.dialogElement.classList.add(...stepClassMap[step])
      );
    }
  }

  /**
   * Scrolls the content of the dialog to the top.
   * Utilizes `fastdom.mutate` to ensure DOM mutations are performed efficiently.
   * This method scrolls the dialog element to the top position (top: 0).
   */
  scrollContentToTop() {
    fastdom.mutate(() => this.dialogElement.scrollTo({ top: 0 }));
  }

  /**
   * Sets up user information by verifying the user's email and updating the user status and user data.
   * If the user's email is available, it triggers an asynchronous verification process.
   * Upon receiving the verification response, it updates the user status and user information streams.
   */
  setUpUserInformation() {
    const user = getStoredUserData();

    if (user.email) {
      this.verifyUserEmail$(user.email).subscribe(data => {
        if (data.status) {
          this.onUserStatusChange$.next(data.status);
        }

        if (data.user) {
          this.onUserChange$.next(data.user);
        }
      });
    }
  }

  // Event Handling Methods
  onStepChange = nextStep => {
    const initialRender = Boolean(!this.currentStep && nextStep);
    this.currentStep = nextStep;

    if (!nextStep) {
      // if nextStep is falsy, close the modal
      this.dialogElement.close();
      this.setDialogStepClass();
      document.body.classList.remove('nhs-u-overflow--hidden');
      return;
    }
    if (initialRender) {
      // Initial render
      this.contentWrapper.innerHTML = this.getHtml();
      this.postInitialRender();
      this.bindEvents();
      this.show();
      this.setUpUserInformation();
      this.setUpUnavailableToursModal();
    } else {
      // Update the panes for the next step
      this.removeNotification();
      this.updatePanes(nextStep);
      this.scrollContentToTop();
    }
    this.setDialogStepClass(nextStep);
    this.emitCustomEvent('domUpdate', {
      step: nextStep,
      initialRender
    });
  };

  subscribe(eventName, callback) {
    this.dialogElement.addEventListener(eventName, callback);
  }

  emitCustomEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, { detail });
    this.dialogElement.dispatchEvent(event);
  }

  /**
   * Sets up and displays a modal indicating that self-guided tours are unavailable.
   *
   * This method disables both the primary and secondary buttons of the modal,
   * updates the modal content with an apology message, and clears any button actions
   * or text. Finally, it displays the modal to the user.
   */
  setUpUnavailableToursModal() {
    if (
      !this.unavailableTours ||
      this.currentStep === ModalStep.SpecSelection
    ) {
      return;
    }

    this.setPrimaryButtonDisabled(true);
    this.setSecondaryButtonDisabled(true);
    const content = `<h1 class="nhs-c-heading--self-guided-tour nhs-u-padding--x-840">We apologize, but self-guided tours are currently unavailable.</h1>`;
    this.innerModal.updateContent(content);
    this.innerModal.updateButtons({
      primaryButtonText: null,
      primaryButtonAction: null,
      secondaryButtonText: null,
      secondaryButtonAction: null,
      secondaryButtonCssClass: null
    });
    this.innerModal.show();
  }

  // Rendering Methods
  setPrimaryButtonDisabled(disabled) {
    this.footer.setPrimaryButtonDisabled(disabled);
  }

  setSecondaryButtonDisabled(disabled) {
    this.footer.setSecondaryButtonDisabled(disabled);
  }

  getNotificationsContainer() {
    return this.dialogElement.querySelector(
      '[data-self-guided-tour-notifications]'
    );
  }

  showNotification(message) {
    const notificationsContainer = this.getNotificationsContainer();
    notificationsContainer.innerHTML = '';
    const notificationElement = document.createElement('div');
    notificationElement.className = `nhs-c-notification__self-guided-error nhs-o-typography--sans-body-1-small-book nhs-u-background-color--peach-300 nhs-u-radius--200 nhs-u-color--black-1000`;
    notificationElement.setAttribute('data-self-guided-error', '');
    notificationElement.innerHTML = message;
    notificationsContainer.appendChild(notificationElement);
  }

  removeNotification() {
    const notificationsContainer = this.getNotificationsContainer();
    if (!notificationsContainer) {
      return;
    } // Ensure the container exists before trying to access it
    const notificationElement = notificationsContainer.querySelector(
      '[data-self-guided-error]'
    );
    if (notificationElement) {
      notificationsContainer.removeChild(notificationElement);
    }
  }

  postInitialRender() {
    // setting up the close button
    const closeButtonElement = this.dialogElement.querySelector(
      '[data-self-guided-tour-dialog-close]'
    );
    fromEvent(closeButtonElement, 'click').subscribe(() => {
      store.dispatch(closeModal());
    });
    this.innerModal.bindEvents();
  }

  show() {
    this.dialogElement.showModal();
    document.body.classList.add('nhs-u-overflow--hidden');
  }

  getMainPane() {
    return document.querySelector(this.mainPaneSelector);
  }

  getComplementaryPane() {
    return document.querySelector(this.complementaryPaneSelector);
  }

  updatePanes(modalStep) {
    this.getMainPane().innerHTML = paneService(
      modalStep,
      'main',
      this
    ).render();
    this.getComplementaryPane().innerHTML = paneService(
      modalStep,
      'complementary',
      this
    ).render();
    // Hide the primary button if there's no label for it (some steps don't need to show a primary button)
    const primaryButtonLabel = primaryButtonTextMap.get(modalStep);
    if (primaryButtonLabel) {
      this.footer.setPrimaryButtonText(primaryButtonLabel);
      this.footer.showPrimaryButton();
    } else {
      this.footer.hidePrimaryButton();
    }
    // Hide the secondary button if there's no label for it (some steps don't need to show a secondary button)
    const secondaryButtonLabel = secondaryButtonTextMap.get(modalStep);
    if (secondaryButtonLabel) {
      this.footer.setSecondaryButtonText(secondaryButtonLabel);
      this.footer.showSecondaryButton(); // it may be good to have some kind of test here
    } else {
      this.footer.hideSecondaryButton();
    }
  }

  /**
   * Handles the booking error by displaying an error message in the inner modal.
   * @param bookingError
   */
  handleBookingError = bookingError => {
    const specDetails = this.getSpecDetails();
    const phone = specDetails.salesNumber;
    const email = specDetails.supportEmail;

    const { copy, headline, copyMobile } =
      SelfGuidedTourModal.getBookingErrorMessage(bookingError, phone, email);

    const content = `<h1 class="nhs-c-heading--self-guided-tour nhs-u-padding--x-840">${headline}</h1>
    <div class="nhs-c-dialog__scrollable-content--auto nhs-u-margin--bottom-200" data-self-guided-tour-tac-modal-content>
      <p class="nhs-c-label--black-900-body-1-medium-regular nhs-mq-display--none-large-max">${copy}</p>
      <p class="nhs-c-label--black-900-body-1-medium-regular nhs-mq-display--none-large">${copyMobile}</p>
    </div>`;

    this.innerModal.updateContent(content);

    this.innerModal.updateButtons(this.getBookingErrorButtons(bookingError));

    this.innerModal.show();
  };

  handleRestart = () => {
    this.innerModal.hide();
    this.innerModal.reset();
    store.dispatch(bookingReset());
  };

  static getBookingErrorMessage(bookingError, phone, email) {
    const formattedPhone = formatPhoneNumber(phone);
    const getUnavailableMessage = (thePhone, theEmail) =>
      `We apologize, but self-guided tours are currently unavailable. To schedule a guided tour of this home, please contact us at ${thePhone} or by email: <a href="mailto:${theEmail}">${theEmail}</a>.`;
    const getUnavailableMobileMessage = theEmail =>
      `We apologize, but self-guided tours are currently unavailable. To schedule a guided tour of this home, please call us, or email us at <a href="mailto:${theEmail}">${theEmail}</a>.`;

    const Messages = {
      [BookingError.TourUnavailable]: {
        headline: 'Self-guided tours are unavailable',
        copy: getUnavailableMessage(formattedPhone, email),
        copyMobile: getUnavailableMobileMessage(email)
      },
      [BookingError.ExceededLimit]: {
        headline: 'Self-guided tours are unavailable',
        copy: getUnavailableMessage(formattedPhone, email),
        copyMobile: getUnavailableMobileMessage(email)
      },
      [BookingError.AccessCode]: {
        headline: 'Access code cannot be generated',
        copy: `There was an issue with scheduling your self-guided tour. To schedule a tour of this home, please contact us at ${formattedPhone} or by email: <a href="mailto:${email}">${email}</a>.`,
        copyMobile: `There was an issue with scheduling your self-guided tour. To schedule a tour of this home, please call us, or email us at <a href="mailto:${email}">${email}</a>.`
      },
      default: {
        headline: 'Self-guided tours are unavailable',
        copy: getUnavailableMessage(formattedPhone, email),
        copyMobile: getUnavailableMobileMessage(email)
      }
    };

    return Messages[bookingError] || Messages.default;
  }

  getBookingErrorButtons(bookingError) {
    const scheduleNewTourText = 'Schedule a new self-tour';
    const scheduleNewTourAction = this.handleRestart;
    const callAssistanceText = 'Call for assistance';
    const callAssistanceAction = this.callSales;
    const hiddenOnDesktopClass = 'nhs-mq-display--none-medium';

    const Messages = {
      [BookingError.ExceededLimit]: {
        primaryButtonText: callAssistanceText,
        primaryButtonAction: callAssistanceAction,
        primaryButtonCssClass: hiddenOnDesktopClass
      },
      [BookingError.AccessCode]: {
        primaryButtonText: callAssistanceText,
        primaryButtonAction: callAssistanceAction,
        primaryButtonCssClass: hiddenOnDesktopClass
      },
      default: {
        primaryButtonText: scheduleNewTourText,
        primaryButtonAction: scheduleNewTourAction,
        secondaryButtonText: callAssistanceText,
        secondaryButtonAction: callAssistanceAction,
        secondaryButtonCssClass: hiddenOnDesktopClass
      }
    };

    return Messages[bookingError] || Messages.default;
  }

  getHtml() {
    return `
    <div class="nhs-c-self-guided-modal">
        <div class="nhs-c-self-guided-modal__content nhs-mq-self-guided-modal__content--large">
          <header class="nhs-c-self-guided-modal__header">
            <div class="nhs-u-display--none nhs-mq-display--flex-large">
              ${nhsLogoSvgElement}
            </div>
            <button type="button" class="nhs-c-modal__close nhs-c-icon__close--24 nhs-u-background-color--transparent" data-self-guided-tour-dialog-close>
              ${closeModalIconSvgElement}          
            </button>
          </header>
          <div class="nhs-c-self-guided-modal__main-content nhs-mq-self-guided-modal__main-content--large" data-self-guided-tour-main>
            ${paneService(this.currentStep, 'main', this).render()}
          </div>
          <div data-self-guided-tour-complementary class="nhs-c-self-guided-modal__complementary-content nhs-mq-self-guided-modal__complementary-content--large">
            ${paneService(this.currentStep, 'complementary', this).render()}
          </div>
          ${this.footer.render()}
        </div>
    </div>
      ${this.innerModal.render()}
    `;
  }
}
